// Legacy `booking` schema dual-fire — preserves the production GA4 funnel
// during the schema transition.
//
// Architecture: the widget emits BOTH the new schema (book_flow_*) and the
// legacy `booking` schema (step 1-5) to sGTM via the same /g/collect
// transport. sGTM's existing GA4 Client parses both naturally; the existing
// GA4 forwarder tag forwards both shapes to GA4 unchanged. No sGTM workspace
// reconfiguration is required as long as the forwarder tag is wired to "All
// Events" (the standard pattern) or has `booking` added to its trigger.
//
// What CC's existing GA4 funnel sees: identical `event_name = booking` events
// with cumulative step params, transported via the resilient first-party
// sGTM path instead of the historical client-side gtag transport. The funnel
// report does not need to be rebuilt.
//
// Mapping (new-schema trigger → legacy step emitted to sGTM):
//   book_flow_contact_info_complete  → booking step=1 (form-submit equivalent)
//   book_flow_loc_type_date_selected → booking step=2 (fitting selection complete)
//   book_flow_time_selected          → booking step=3 (appointment selection complete)
//   book_flow_submit                 → booking step=4 (reservation summary)
//   book_flow_complete               → booking step=5 (booking confirmation)
//
// Legacy step 4.1 (extended profile fields like junior_fitting, newsletter,
// hand_dominance) is intentionally skipped — the new widget doesn't collect
// most of those fields. The funnel populates correctly without it.
//
// Enabled by default. The dual-fire is the intended behavior during the
// schema-transition window so the legacy funnel keeps populating. To
// disable (e.g., at sunset, or in a dev environment that shouldn't
// double-emit), set window.CC_WIDGET_CONFIG.legacyDualFire = false on
// the host page.

import type { EventParams } from './events';
import { postToSgtm } from './dispatch';

interface LegacyState {
  location?: string;
  fitting_selection?: string;
  date_and_time?: string;
  time_zone?: string;
  hand_dominance?: string;
  referral_method_primary?: string;
  referral_method_secondary?: string;
  referral_capture?: 'auto' | 'user_reported';
  junior_fitting?: boolean;
  promo_code?: string;
  store?: string;
  value?: number;
  currency?: string;
}

const STEP_NAMES: Record<string, string> = {
  '1': 'form submitted - website',
  '2': 'fitting selection - salesforce',
  '3': 'appointment selection - salesforce',
  '4': 'reservation summary - salesforce',
  '5': 'booking confirmation - salesforce',
};

const legacyState: LegacyState = {};
const firedSteps = new Set<string>();

/**
 * Capture referral attribution from the parent page URL on first emit.
 * Legacy SF SchedulingApp populated `referral_method_*` from a user-reported
 * "How did you hear about us?" form field. The new widget doesn't ask, so
 * we substitute technical attribution: utm_source/utm_medium with referrer
 * fallback. `referral_capture: 'auto'` flags the substitution so dashboards
 * can distinguish from legacy user-reported data.
 */
function captureReferralOnce(): void {
  if (legacyState.referral_method_primary !== undefined) return;
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');

  let primary = utmSource ?? undefined;
  if (!primary && document.referrer) {
    try {
      primary = new URL(document.referrer).hostname;
    } catch {
      primary = undefined;
    }
  }

  legacyState.referral_method_primary = primary ?? '(direct)';
  legacyState.referral_method_secondary = utmMedium ?? '(none)';
  legacyState.referral_capture = 'auto';
}

function accumulate(payload: EventParams): void {
  const p = payload as unknown as Record<string, unknown>;

  if (typeof p.location === 'string') legacyState.location = p.location;
  if (typeof p.fitting_selection === 'string') legacyState.fitting_selection = p.fitting_selection;
  if (typeof p.time_zone === 'string') legacyState.time_zone = p.time_zone;
  if (typeof p.hand_dominance === 'string') legacyState.hand_dominance = p.hand_dominance;
  if (typeof p.referral_method_primary === 'string')
    legacyState.referral_method_primary = p.referral_method_primary;
  if (typeof p.referral_method_secondary === 'string')
    legacyState.referral_method_secondary = p.referral_method_secondary;
  if (typeof p.junior_fitting === 'boolean') legacyState.junior_fitting = p.junior_fitting;
  if (typeof p.promo_code === 'string') legacyState.promo_code = p.promo_code;
  if (typeof p.value === 'number') legacyState.value = p.value;
  if (typeof p.currency === 'string') legacyState.currency = p.currency;
  if (typeof p.store === 'string') legacyState.store = p.store;

  // date_and_time = date_selected + time_selected (when both are present in
  // the same event payload). Matches the legacy combined-string format.
  if (typeof p.date_selected === 'string') {
    const time = typeof p.time_selected === 'string' ? p.time_selected : '';
    legacyState.date_and_time = time ? `${p.date_selected}T${time}` : p.date_selected;
  }
}

/**
 * Emit a legacy `booking` step event to sGTM. Once-per-session per step.
 * channelmix_client_id is included so it's routed to the GA4 user_property
 * surface by the shared transport.
 */
function emitLegacyStep(step: string, extra: Record<string, unknown> = {}): void {
  if (firedSteps.has(step)) return;
  firedSteps.add(step);

  const params: Record<string, unknown> = {
    step,
    step_name: STEP_NAMES[step],
    ...legacyState,
    ...extra,
  };

  // ChannelMix client ID — the shared transport recognizes this key and
  // routes it to up.channelmix_client_id (user-scoped) per GA4 MP shape.
  if (typeof window !== 'undefined') {
    const cmxId = (window.CC_WIDGET_CONFIG ?? {}).channelmixClientId;
    if (cmxId) params.channelmix_client_id = cmxId;
  }

  for (const k of Object.keys(params)) {
    if (params[k] === undefined) delete params[k];
  }

  postToSgtm('booking', params);
}

/**
 * Called by the main dispatch after each new-schema event. Updates the
 * cumulative legacy state and emits legacy events at the right transition
 * points. Enabled by default — set
 * window.CC_WIDGET_CONFIG.legacyDualFire = false to disable.
 */
export function maybeEmitLegacy(payload: EventParams): void {
  if (typeof window === 'undefined') return;
  if (window.CC_WIDGET_CONFIG?.legacyDualFire === false) return;

  captureReferralOnce();
  accumulate(payload);

  switch (payload.event) {
    case 'book_flow_contact_info_complete':
      // Legacy step 1 historically arrived at GA4 as event_name=booking,
      // step=1 (the Web Container's Booking 1 tag re-fired the dataLayer
      // form_submit as a `booking` event). We emit the same GA4 shape
      // directly via sGTM.
      emitLegacyStep('1', {
        form_id: 'CMwebsubmit',
        form_name: payload.form_name ?? 'CC Booking Widget',
        channelmix_timestamp: new Date().toISOString(),
      });
      break;

    case 'book_flow_loc_type_date_selected':
      emitLegacyStep('2');
      break;

    case 'book_flow_time_selected':
      emitLegacyStep('3');
      break;

    case 'book_flow_submit':
      emitLegacyStep('4');
      break;

    case 'book_flow_complete':
      emitLegacyStep('5', {
        confirmation_number: payload.confirmation_number,
        // SMS opt-in defaults to false at conversion time. The new widget
        // collects SMS opt-in on a separate post-booking step; its true
        // value lives on `post_booking_completed` in the new schema.
        sms_reminder: false,
        // waitlisted (legacy field name) carries the user's path through
        // the flow — 'WL' if joined via the no-availability empty state,
        // 'scheduled' otherwise. Closes the legacy `waitlisted` TBD from
        // the schema audit.
        waitlisted: payload.waitlist,
      });
      break;
  }
}

/** Test-only reset. Not used in production. */
export function _resetLegacyState(): void {
  firedSteps.clear();
  for (const k of Object.keys(legacyState)) {
    delete (legacyState as Record<string, unknown>)[k];
  }
}
